import * as React from 'react';
import { defaultMemoize } from 'reselect';
import { ServiceLocator } from '../../app/ServiceLocator';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '../../app/spaces/SpaceTelemetry';
import { Subspace, TabbedSpace } from '../../app/spaces/TabbedSpace';
import { DataRoleSubspaceName } from '../../app/state/SpaceInfo';
import { Messages } from '../../codegen/Localize';
import { ReactDirectiveProps } from '../../ts/directives/reactDirectiveFactory';
import { IDataRoleDetailInfoWithUsage } from '../../ts/models/IDataRoleInfo';
import { FieldConceptIndexingStatusCode, GetFieldConceptIndexingStatusRequest, IGetFieldConceptIndexingStatusResult } from '../../ts/ServerApi/requests/GetFieldConceptIndexingStatus';
import { IDataRoleActions } from '../../ts/ServerApi/types/IDataRoleActions';
import { ServerService } from '../../ts/services/ServerService';
import { Telemetry } from '../../ts/services/TelemetryService';
import { DataRoleSubspaceProps } from '../types/DataRoleSubspaceProps';
import { DefinitionSubspace } from './DataRoleDefinitionSubspace';
import { DataRoleSpaceHeader } from './DataRoleSpaceHeader';

export type DataRoleSpaceStateProps = {
  readonly urls: SpacesSiteUrls;
};

export type DataRoleSpaceDispatchProps = {
  readonly enterSpace: () => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
};

export interface DataRoleSpaceOwnProps extends ReactDirectiveProps {
  readonly dataRoleDetailInfoWithUsage: IDataRoleDetailInfoWithUsage;
  readonly allowedActions: IDataRoleActions;

  // subspace info
  readonly subspaceName: string;
  readonly subspaceProps: DataRoleSubspaceProps;

  // Angular services
  readonly telemetry: Telemetry;
}

export type DataRoleSpaceProps = DataRoleSpaceStateProps & DataRoleSpaceDispatchProps & DataRoleSpaceOwnProps;

export interface DataRoleSpaceState {
  readonly fieldConceptIndexingStatusCode: FieldConceptIndexingStatusCode;
}

function definitionSubspace(subspaceProps: DataRoleSubspaceProps): Subspace {
  return {
    name: 'definition',
    tabLabel: Messages.definition(),
    renderContent: () => <DefinitionSubspace {...subspaceProps}/>,
    shouldRenderTabContentCount: false
  };
}

function getSubspaces(subspaceProps:  DataRoleSubspaceProps): ReadonlyArray<Subspace> {
  return [definitionSubspace(subspaceProps)];
}

export class DataRoleSpace extends React.PureComponent<DataRoleSpaceProps, DataRoleSpaceState> {

  private memoizedGetSubspaces = defaultMemoize(getSubspaces);
  private server: ServerService;
  public state: DataRoleSpaceState = { fieldConceptIndexingStatusCode: 'INDEXING_STATUS_UNSPECIFIED' };
  constructor(props: DataRoleSpaceProps) {
    super(props);
    this.server = ServiceLocator.get('ServerService');
    props.enterSpace();
  }

  public urlForSubspace = (subspaceName?: DataRoleSubspaceName)  => {
    // This is trivial until more subspaces get created in 19.4
    return this.props.urls.dataRoleUrl(this.props.dataRoleDetailInfoWithUsage.id);
  }

  private updateFieldConceptIndexingStatus = () => {
    this.server.sendRequest(new GetFieldConceptIndexingStatusRequest({
      field_concept_uri: this.props.dataRoleDetailInfoWithUsage.fieldConceptUri
    }))
    .then((status: IGetFieldConceptIndexingStatusResult) => {
      this.setState({fieldConceptIndexingStatusCode: status.fieldConceptIndexingStatusCode});
      if(retryStatusCodes.indexOf(status.fieldConceptIndexingStatusCode) > -1) {
        setTimeout(this.updateFieldConceptIndexingStatus, 10000);
      }
    })
    .catch(() => null);
  }

  public render() {
    const subspaces = this.memoizedGetSubspaces(this.props.subspaceProps);

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.subspaceName as DataRoleSubspaceName}
        urlForSubspace={this.urlForSubspace}
        mastheadElement={this.mastheadElement}
        testIdPrefix={'data-role'}
        spaceItemName={this.props.dataRoleDetailInfoWithUsage.name}
      />
    );
  }

  public componentDidMount() {
    this.handleTelemetry();
    this.updateFieldConceptIndexingStatus();
  }

  public componentDidUpdate(prevProps: DataRoleSpaceProps, prevState: DataRoleSpaceState) {
    if (this.props.subspaceName !== prevProps.subspaceName) {
      this.props.enterSpace();
    }
    if (this.props.dataRoleDetailInfoWithUsage.id !== prevProps.dataRoleDetailInfoWithUsage.id) {
      this.handleTelemetry();
    }
    if (retryStatusCodes.indexOf(prevState.fieldConceptIndexingStatusCode) > -1 && retryStatusCodes.indexOf(prevState.fieldConceptIndexingStatusCode) === -1) {
      location.reload(); // When we finish loading, refresh and get the latest list of fields
    }
  }

  private handleTelemetry() {
    maybeSendTelemetry_favoriteItemOpened(this.props.telemetry, this.props.dataRoleDetailInfoWithUsage);
  }

  private get mastheadElement(): React.ReactNode {
    return <DataRoleSpaceHeader
      availableActions={this.props.allowedActions}
      item={this.props.dataRoleDetailInfoWithUsage}
      urls={this.props.urls}
      addToFavorites={this.props.addToFavorites}
      removeFromFavorites={this.props.removeFromFavorites}
      fieldConceptIndexingStatusCode={this.state.fieldConceptIndexingStatusCode}
    />;
  }
}

// If computation is still in progress, we should update periodically to get the latest status
const retryStatusCodes: ReadonlyArray<FieldConceptIndexingStatusCode> = [
  'INDEXING_IN_PROGRESS',
  'INDEXING_PENDING',
  'INDEXING_FAILED_RETRY_PENDING',
];
